import { forwardRef, useState } from 'react';
import { useTranslate } from 'hooks/translation-hooks';
import { Controller } from 'react-hook-form';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';

import { DatePickerTitle, KeyboardDatePicker } from './DatePickerStyled';
import DayJS from '@date-io/dayjs';
import { useSelector } from 'react-redux';
import { selectLanguage } from 'redux/selectors/config';
import { getLocale } from 'containers/SynergyFollowConfirmation/utils';
import { ga4Event } from 'analytics';
import { CalendarIcon } from '@integration/alexandria-ui';

const DatePickerSelector = forwardRef((props, ref) => {
  const {
    name,
    translateOrigin,
    changeDate,
    shouldDisableDate,
    disableFuture,
    disablePast,
    gaZone,
    id = 'date-picker-dialog',
    buttonProps,
    renderProps = {},
    startDate,
  } = props;
  const { field } = renderProps;
  const t = useTranslate(translateOrigin);
  const language = useSelector(selectLanguage);
  const [isOpen, setOpen] = useState(false);
  const locale = getLocale(language);
  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
      <KeyboardDatePicker
        ref={ref || field?.ref}
        disablePast={disablePast}
        allowKeyboardControl={false}
        label=""
        margin="normal"
        id={id}
        disableFuture={disableFuture}
        format="dd/MM/yyyy"
        okLabel={t(`${name}_calendar-accept_button`)}
        cancelLabel={t(`${name}_calendar-cancel_button`)}
        invalidDateMessage={t(`${name}_page-invalid_date_text`)}
        minDateMessage={t(`${name}_page-invalid_date_text`)}
        maxDateMessage={t(`${name}_page-invalid_date_text`)}
        invalidLabel={t(`${name}_page-invalid_date_text`)}
        emptyLabel={t(`${name}_page-invalid_date_text`)}
        shouldDisableDate={shouldDisableDate}
        KeyboardButtonProps={buttonProps}
        InputProps={{ readOnly: true }}
        keyboardIcon={<CalendarIcon />}
        inputProps={{ 'data-testid': id }}
        onClick={() => {
          setOpen(true);
          ga4Event({
            eventaction: 'display',
            payload: {
              element_displayed: 'popup',
              element_name: gaZone,
            },
          });
        }}
        onClose={() => {
          setOpen(false);
          ga4Event({
            eventaction: 'navigation',
            payload: {
              zone: `popup_${gaZone}`,
              element_clicked: 'cancel',
            },
          });
        }}
        open={isOpen}
        name={name || field.name}
        value={field?.value || startDate}
        onBlur={field?.onBlur}
        inputRef={field?.ref}
        onChange={e => {
          const onChangeFunction = changeDate || field?.onChange;
          if (onChangeFunction) {
            onChangeFunction(e);
          }
          ga4Event({
            eventaction: 'navigation',
            payload: {
              zone: `popup_${gaZone}`,
              element_clicked: 'apply',
            },
          });
        }}
      />
    </MuiPickersUtilsProvider>
  );
});

const DatePickerInput = props => {
  const {
    caseCreationCustomStep,
    translateOrigin,
    name,
    title,
    control,
    relevantData,
  } = props;
  const t = useTranslate(translateOrigin);
  const language = useSelector(selectLanguage);
  const locale = getLocale(language);
  return (
    <MuiPickersUtilsProvider utils={DayJS} locale={locale}>
      <DatePickerTitle caseCreationCustomStep={caseCreationCustomStep}>
        {t(title, relevantData)}
      </DatePickerTitle>
      {control ? (
        <Controller
          name={name}
          control={control}
          render={renderProps => (
            <DatePickerSelector {...props} renderProps={renderProps} />
          )}
        />
      ) : (
        <DatePickerSelector {...props} />
      )}
    </MuiPickersUtilsProvider>
  );
};

export default DatePickerInput;
